import React, { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronLeft,
  ChevronRight,
  Stethoscope,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  X,
  Layers
} from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { MultiSelect } from "../components/ui/multi-select";
import useToast from "../hooks/useToast";
import { uploadFileToS3 } from "../services/uploadService";
import { getCategories } from "../services/categoryService";
import {
  createDoctor,
  deleteDoctor,
  getDoctors,
  updateDoctor
} from "../services/doctorService";

const doctorSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  designation: z.string().trim().min(1, "Designation is required"),
  profile_image: z.string().trim().optional(),
  short_description: z.string().trim().optional()
});

const defaultValues = {
  name: "",
  designation: "",
  profile_image: "",
  short_description: ""
};

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function truncateText(text = "", maxLength = 90) {
  if (!text) return "--";
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
  if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
  if (currentPage >= totalPages - 3) return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

export default function Doctor() {
  const [doctors, setDoctors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDeletingDoctor, setIsDeletingDoctor] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDoctorId, setEditingDoctorId] = useState(null);
  const [doctorPendingDelete, setDoctorPendingDelete] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  
  const fileInputRef = useRef(null);
  const { showErrorToast, showSuccessToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues,
    resolver: zodResolver(doctorSchema)
  });

  const profileImage = watch("profile_image");
  const previewName = watch("name");

  const refreshListing = (targetPage = pagination.page, targetLimit = pagination.limit) => {
    if (targetPage !== pagination.page || targetLimit !== pagination.limit) {
      setPagination((current) => ({ ...current, page: targetPage, limit: targetLimit }));
      return;
    }
    loadDoctors(targetPage, targetLimit);
  };



  async function loadDoctors(pageArg = pagination.page, limitArg = pagination.limit) {
    setIsLoading(true);
    const response = await getDoctors({ page: pageArg, limit: limitArg });
    if (response?.success) {
      setDoctors(response.data.doctors || []);
      setPagination({
        page: response.data.pagination?.page || pageArg,
        limit: response.data.pagination?.limit || limitArg,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1
      });
    } else {
      showErrorToast(response?.message || "Unable to load doctors");
    }
    setIsLoading(false);
  }

  useEffect(() => {
    loadDoctors(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  const openCreateModal = () => {
    reset(defaultValues);
    setEditingDoctorId(null);
    setIsDragActive(false);
    setIsUploadingImage(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    reset(defaultValues);
    setEditingDoctorId(null);
    setIsDragActive(false);
    setIsUploadingImage(false);
    setIsModalOpen(false);
  };

  const handleFileRead = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showErrorToast("Please upload an image file");
      return;
    }

    setIsUploadingImage(true);
    uploadFileToS3(file, "doctors").then((response) => {
      if (!response?.success) {
        showErrorToast(response?.message || "Unable to upload image");
        setIsUploadingImage(false);
        return;
      }
      setValue("profile_image", response.data.file.url, { shouldDirty: true, shouldValidate: true });
      showSuccessToast("Image uploaded successfully");
      setIsUploadingImage(false);
    });
  };

  const handleEdit = (doctor) => {
    setEditingDoctorId(doctor.id);
    reset({
      name: doctor.name,
      designation: doctor.designation,
      profile_image: doctor.profile_image || "",
      short_description: doctor.short_description || ""
    });
    setIsDragActive(false);
    setIsUploadingImage(false);
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!doctorPendingDelete) return;
    setIsDeletingDoctor(true);
    const response = await deleteDoctor(doctorPendingDelete.id);
    setIsDeletingDoctor(false);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to delete doctor");
      return;
    }

    const nextPage = doctors.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
    showSuccessToast(response.message || "Doctor deleted successfully");
    setDoctorPendingDelete(null);
    refreshListing(nextPage, pagination.limit);
  };

  const onSubmit = async (values) => {
    const payload = {
      ...values
    };

    const response = editingDoctorId
      ? await updateDoctor(editingDoctorId, payload)
      : await createDoctor(payload);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to save doctor");
      return;
    }

    showSuccessToast(response.message || "Doctor saved successfully");
    closeModal();
    if (editingDoctorId) refreshListing(pagination.page, pagination.limit);
    else refreshListing(1, pagination.limit);
  };

  const startItem = pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem = pagination.total ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const pageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Medical Module"
        title="Doctor"
        description="Manage doctor records."
        badge={`${pagination.total} Profiles`}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Doctor Listing</CardTitle>
            <p className="mt-2 text-sm text-panel-muted text-panel-muted">View records below.</p>
          </div>
          <Button type="button" onClick={openCreateModal}>
            <Plus className="mr-2 h-4 w-4" /> Add Doctor
          </Button>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="rounded-3xl border border-slate-100 bg-slate-50/80 p-6 text-sm text-panel-muted">Loading...</div>
          ) : doctors.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="bg-slate-50/90">
                      <tr className="text-left">
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Profile</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Name</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {doctors.map((doctor) => (
                        <tr key={doctor.id} className="border-t border-slate-100 align-middle">
                          <td className="px-5 py-4">
                            {doctor.profile_image ? (
                              <img src={doctor.profile_image} alt={doctor.name} className="h-12 w-12 rounded-xl object-cover" />
                            ) : (
                              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-xs font-bold text-white">
                                {getInitials(doctor.name)}
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-4">
                            <div className="font-semibold text-panel-ink">{doctor.name}</div>
                            <div className="text-xs text-primary">{doctor.designation}</div>
                          </td>

                          <td className="px-5 py-4 text-right">
                            <div className="flex justify-end gap-2">
                              <Button type="button" size="sm" variant="outline" onClick={() => handleEdit(doctor)}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button type="button" size="sm" variant="ghost" className="text-rose-500" onClick={() => setDoctorPendingDelete(doctor)}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-slate-50/70 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-panel-muted">
                  Showing <span className="font-semibold text-panel-ink">{startItem}</span> to <span className="font-semibold text-panel-ink">{endItem}</span> of <span className="font-semibold text-panel-ink">{pagination.total}</span> doctors
                </p>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => refreshListing(pagination.page - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {pageNumbers.map((p, i) => (
                    p === "..." ? <span key={i} className="text-sm">...</span> : 
                    <Button key={i} size="sm" variant={pagination.page === p ? "default" : "outline"} onClick={() => refreshListing(p)}>{p}</Button>
                  ))}
                  <Button type="button" variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => refreshListing(pagination.page + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 p-10 text-center">
              <p className="text-lg font-semibold mb-4 text-panel-ink">No Doctors added yet.</p>
              <Button type="button" onClick={openCreateModal}><Plus className="mr-2 h-4 w-4" /> Add Your First Doctor</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 overflow-y-auto">
          <div className="max-h-[95dvh] w-full max-w-4xl overflow-y-auto rounded-[32px] border bg-white shadow-2xl p-6">
            <div className="flex items-center justify-between border-b pb-4 mb-6">
              <h2 className="text-2xl font-bold">{editingDoctorId ? "Edit Doctor" : "Add Doctor"}</h2>
              <button type="button" onClick={closeModal}><X /></button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8">
              <div className="space-y-6">
                <div 
                  className={`relative aspect-square rounded-[32px] border-2 border-dashed flex flex-col items-center justify-center text-center p-4 transition-all ${isDragActive ? "border-primary bg-primary/5" : "border-slate-200 bg-slate-50"}`}
                  onDragOver={(e) => { e.preventDefault(); setIsDragActive(true); }}
                  onDragLeave={() => setIsDragActive(false)}
                  onDrop={(e) => { e.preventDefault(); setIsDragActive(false); handleFileRead(e.dataTransfer.files[0]); }}
                >
                  {profileImage ? (
                    <img src={profileImage} alt="Profile" className="absolute inset-0 h-full w-full object-cover rounded-[30px]" />
                  ) : (
                    <div className="text-slate-400">
                      <Upload className="mx-auto h-8 w-8 mb-2" />
                      <p className="text-xs font-semibold px-2">Drag profile image here</p>
                    </div>
                  )}
                  <div className="absolute bottom-4 inset-x-4">
                    <Button type="button" variant="white" size="sm" className="w-full shadow-sm" onClick={() => fileInputRef.current?.click()} disabled={isUploadingImage}>
                      {isUploadingImage ? "Uploading..." : profileImage ? "Change Image" : "Upload Image"}
                    </Button>
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileRead(e.target.files[0])} />
                </div>
              </div>

              <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 text-panel-ink">
                    <FieldLabel>Full Name *</FieldLabel>
                    <Input placeholder="Dr. John Doe" {...register("name")} />
                    {errors.name && <FieldError>{errors.name.message}</FieldError>}
                  </div>
                  <div className="space-y-2 text-panel-ink">
                    <FieldLabel>Designation *</FieldLabel>
                    <Input placeholder="Senior Consultant" {...register("designation")} />
                    {errors.designation && <FieldError>{errors.designation.message}</FieldError>}
                  </div>
                </div>



                <div className="space-y-2 text-panel-ink">
                  <FieldLabel>Short Description</FieldLabel>
                  <Textarea placeholder="Brief bio or expertise..." className="min-h-[140px]" {...register("short_description")} />
                </div>

                <div className="flex gap-4 pt-6 border-t font-semibold">
                  <Button type="submit" className="px-10" disabled={isSubmitting || isUploadingImage}>
                    {isSubmitting ? "Saving..." : "Save Doctor Profile"}
                  </Button>
                  <Button type="button" variant="outline" onClick={closeModal}>Cancel</Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {doctorPendingDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-[32px] border bg-white p-6 shadow-xl text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 mx-auto mb-4">
              <Trash2 className="h-7 w-7" />
            </div>
            <h2 className="text-2xl font-bold">Delete Profile?</h2>
            <p className="mt-3 text-panel-muted">Deleting <span className="font-bold text-panel-ink">{doctorPendingDelete.name}</span> is permanent.</p>
            <div className="mt-8 flex gap-4">
              <Button type="button" variant="destructive" className="flex-1" onClick={handleDelete} disabled={isDeletingDoctor}>
                {isDeletingDoctor ? "Deleting..." : "Confirm Delete"}
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDoctorPendingDelete(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
